<template>
    <div class="box">
        <div class="box-header">
            <h3 class="box-title">Liste des clubs et leurs labels</h3>
        </div>
        <div class="box-body">
            <div v-if="loading" class="loading-container">
                <i class="fa fa-spinner fa-spin fa-2x"></i>
                <span>Chargement des clubs...</span>
            </div>
            <div v-else class="table-responsive">
                <table class="table table-bordered table-striped">
                    <thead>
                        <tr>
                            <th @click="sortBy('club')" :data-sort="sortKey === 'club' ? (sortOrder > 0 ? 'asc' : 'desc') : null">Club</th>
                            <th @click="sortBy('Art Martial')">Art Martial</th>
                            <th @click="sortBy('Kyorugi')">Kyorugi</th>
                            <th @click="sortBy('Poomsae')">Poomsae</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="club in sortedClubs">
                            <tr :key="club.id">
                                <td><a :href="'club/' + club.id">{{ club.numero }} - {{ club.club }}</a></td>
                                <td>
                                    <div class="label-status" :class="getLabelStatusClass(club, 'Art Martial')">
                                        {{ getLabelStatusText(club, 'Art Martial') }}
                                    </div>
                                </td>
                                <td>
                                    <div class="label-status" :class="getLabelStatusClass(club, 'Kyorugi')">
                                        {{ getLabelStatusText(club, 'Kyorugi') }}
                                    </div>
                                </td>
                                <td>
                                    <div class="label-status" :class="getLabelStatusClass(club, 'Poomsae')">
                                        {{ getLabelStatusText(club, 'Poomsae') }}
                                    </div>
                                </td>
                                <td>
                                    <button class="btn btn-sm btn-info" @click="toggleCriteres(club.id)">
                                        <i class="fa" :class="expandedClubs.includes(club.id) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                                    </button>
                                </td>
                            </tr>
                            <tr v-if="expandedClubs.includes(club.id)" :key="'crit-' + club.id">
                                <td colspan="6">
                                    <div class="criteres-container">
                                        <h5>Critères obtenus</h5>
                                        <table class="table table-bordered table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Catégorie</th>
                                                    <th>Sous-catégorie</th>
                                                    <th>Critère</th>
                                                    <th>Date début</th>
                                                    <th>Date fin</th>
                                                    <th>Commentaire</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="critere in club.criteres" :key="critere.id">
                                                    <td>{{ critere.critere.categorie }}</td>
                                                    <td>{{ critere.critere.sous_categorie }}</td>
                                                    <td>{{ critere.critere.nom }}</td>
                                                    <td>{{ formatDate(critere.date_debut) }}</td>
                                                    <td>{{ formatDate(critere.date_fin) }}</td>
                                                    <td>{{ critere.commentaire || '-' }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import {mapGetters} from 'vuex'

export default {
    name: 'LabelisationsEffectuees',
    data() {
        return {
            clubs: [],
            expandedClubs: [],
            loading: false,
            sortKey: 'club',
            sortOrder: 1
        }
    },
    computed: {
        ...mapGetters({
            getRoute: 'getRoute'
        }),
        sortedClubs() {
            return [...this.clubs].sort((a, b) => {
                if (this.sortKey === 'club') {
                    const aValue = a.numero;
                    const bValue = b.numero;
                    return this.sortOrder * (aValue > bValue ? 1 : -1);
                }
                return 0;
            });
        }
    },
    mounted() {
        this.getClubs();
    },
    methods: {
        getClubs() {
            this.loading = true;
            this.$http.get(this.getRoute('Labelisation.getAllClubsWithCanHaveLabel'))
                .then(response => {
                    this.clubs = response.body;
                })
                .catch(error => {
                    console.error('Erreur lors de la récupération des clubs:', error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        toggleCriteres(clubId) {
            const index = this.expandedClubs.indexOf(clubId);
            if (index === -1) {
                this.expandedClubs.push(clubId);
            } else {
                this.expandedClubs.splice(index, 1);
            }
        },
        getLabelStatusClass(club, labelNom) {
            const currentLevel = this.getCurrentLabelLevel(club, labelNom);
            const eligibleLevel = this.getEligibleLabelLevel(club, labelNom);

            if (currentLevel === eligibleLevel && currentLevel > 0) {
                return 'text-success';
            } else if (eligibleLevel > currentLevel) {
                return 'text-warning';
            }
            return 'text-muted';
        },
        getLabelStatusText(club, labelNom) {
            const currentLevel = this.getCurrentLabelLevel(club, labelNom);
            const eligibleLevel = this.getEligibleLabelLevel(club, labelNom);

            if (currentLevel > 0) {
                if (eligibleLevel > currentLevel) {
                    return `${currentLevel} étoile${currentLevel > 1 ? 's' : ''} (Éligible ${eligibleLevel} étoile${eligibleLevel > 1 ? 's' : ''})`;
                }
                return `${currentLevel} étoile${currentLevel > 1 ? 's' : ''}`;
            } else if (eligibleLevel > 0) {
                return `Éligible ${eligibleLevel} étoile${eligibleLevel > 1 ? 's' : ''}`;
            }
            return 'Non éligible';
        },
        getCurrentLabelLevel(club, labelNom) {
            const canHaveLabel = club.canHaveLabel.original;
            if (!canHaveLabel.labelsObtenusParLeClub) return 0;
            
            const now = new Date();
            const labelActif = canHaveLabel.labelsObtenusParLeClub.find(label => {
                if (label.label !== labelNom) return false;
                const dateFin = new Date(label.date_fin);
                return dateFin > now;
            });
            
            return labelActif ? labelActif.niveau : 0;
        },
        getEligibleLabelLevel(club, labelNom) {
            const canHaveLabel = club.canHaveLabel.original.labelsFinalsEligibleAvecCommun;
            if (!canHaveLabel[labelNom]) return 0;
            
            for (let i = 3; i >= 1; i--) {
                if (canHaveLabel[labelNom][i]) {
                    return i;
                }
            }
            return 0;
        },
        formatDate(date) {
            return new Date(date).toLocaleDateString('fr-FR');
        },
        sortBy(key) {
            if (this.sortKey === key) {
                this.sortOrder *= -1;
            } else {
                this.sortKey = key;
                this.sortOrder = 1;
            }
        }
    }
}
</script>

<style scoped>
.label-status {
    padding: 5px 10px;
    border-radius: 4px;
    text-align: center;
    font-weight: 500;
}

.text-success {
    color: #28a745;
    background-color: rgba(40, 167, 69, 0.1);
}

.text-warning {
    color: #ffc107;
    background-color: rgba(255, 193, 7, 0.1);
}

.text-muted {
    color: #6c757d;
    background-color: rgba(108, 117, 125, 0.1);
}

.criteres-container {
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 4px;
    margin: 10px 0;
}

.criteres-container h5 {
    margin-bottom: 15px;
    color: #333;
}

.table-sm td {
    padding: 8px;
    font-size: 13px;
}

.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    gap: 15px;
    color: #666;
}

.loading-container i {
    color: #007bff;
}

th {
    cursor: pointer;
    user-select: none;
    position: relative;
    padding-right: 20px;
}

th:hover {
    background-color: #f5f5f5;
}

th::after {
    content: '↕';
    position: absolute;
    right: 5px;
    color: #999;
    font-size: 12px;
}

th[data-sort="asc"]::after {
    content: '↑';
}

th[data-sort="desc"]::after {
    content: '↓';
}
</style> 


// WEBPACK FOOTER //
// src/components/Labelisation/LabelisationsEffectuees.vue